/**
 * Contrôle « Corrigé dicté » — partagé par le wizard de création V2 et le
 * panneau de génération de l'éditeur V2.
 *
 * Machine d'état : record (idle) → recording → transcribing → transcript (éditable).
 * L'enseignant dicte, on transcrit via `transcribe-correction` (Whisper, audio
 * jamais stocké), il relit/corrige la transcription, puis « Structurer » appelle
 * `onStructure(transcript)` (le parent lance la phase `exercise-v2-dictation`).
 */

import { useCallback, useState } from 'react';
import { Mic, Square, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { transcribeDictation } from '@/lib/exercise-v2/generation-client';
import { useAudioRecorder } from './use-audio-recorder';
import { isLikelyVirtualMic } from './mic-utils';

type Phase = 'record' | 'transcribing' | 'transcript';

export interface DictationCorrectionControlProps {
  /** Lance la structuration du corrigé à partir de la transcription validée. */
  onStructure: (transcript: string) => Promise<void>;
  /** La structuration (phase dictée) est en cours. */
  structuring?: boolean;
  /** Erreur remontée par la structuration. */
  structureError?: string | null;
  /** Libellé du bouton de structuration. */
  structureLabel?: string;
  /** Désactive l'ensemble (ex. une autre phase tourne). */
  disabled?: boolean;
  /** Transcription mémorisée d'une dictée précédente : affichée + éditable d'emblée. */
  initialTranscript?: string;
}

function fmt(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function DictationCorrectionControl({
  onStructure,
  structuring = false,
  structureError = null,
  structureLabel = 'Structurer le corrigé',
  disabled = false,
  initialTranscript = '',
}: DictationCorrectionControlProps) {
  const recorder = useAudioRecorder();
  const hasInitial = initialTranscript.trim().length > 0;
  const [phase, setPhase] = useState<Phase>(hasInitial ? 'transcript' : 'record');
  const [transcript, setTranscript] = useState(initialTranscript);
  const [error, setError] = useState<string | null>(null);

  const handleStop = useCallback(async () => {
    const { blob, hadSignal } = await recorder.stop();
    if (!blob) {
      setError('Aucun son enregistré.');
      setPhase('record');
      return;
    }
    if (!hadSignal) {
      const wasVirtual = recorder.activeLabel ? isLikelyVirtualMic(recorder.activeLabel) : false;
      setError(
        wasVirtual
          ? `« ${recorder.activeLabel} » est un micro VIRTUEL (Teams, OBS…) qui ne capte rien. J'ai sélectionné ton micro physique dans la liste — réessaie.`
          : "Le micro n'a capté aucun son. Vérifie qu'il n'est pas coupé, que c'est le bon périphérique d'entrée, et parle plus près, puis réessaie.",
      );
      setPhase('record');
      return;
    }
    setPhase('transcribing');
    setError(null);
    try {
      const { text } = await transcribeDictation(blob);
      setTranscript(text);
      setPhase('transcript');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Transcription impossible.');
      setPhase('record');
    }
  }, [recorder]);

  const reset = useCallback(() => {
    recorder.cancel();
    setTranscript('');
    setError(null);
    setPhase('record');
  }, [recorder]);

  const busy = disabled || structuring;
  const shownError = error ?? recorder.error ?? structureError;

  return (
    <div className="border-ink-200 bg-ink-50/40 mt-3 rounded-2xl border p-3">
      {phase === 'record' && recorder.state === 'idle' && (
        <div className="flex flex-col gap-2">
          {recorder.devices.length > 0 && (
            <label className="flex items-center gap-2 font-sans text-[12px]">
              <Mic className="text-ink-400 h-3.5 w-3.5 shrink-0" />
              <span className="text-ink-500 shrink-0">Micro :</span>
              <select
                value={recorder.deviceId ?? ''}
                onChange={(e) => recorder.setDeviceId(e.target.value || null)}
                disabled={busy}
                className="border-ink-200 min-h-9 flex-1 rounded-lg border bg-white px-2 font-sans text-[12px]"
              >
                <option value="">Micro par défaut du système</option>
                {recorder.devices.map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId}>
                    {(d.label || `Micro ${i + 1}`) +
                      (d.label && isLikelyVirtualMic(d.label) ? ' — virtuel ⚠️' : '')}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => void recorder.start()}
            disabled={busy}
            className="w-full active:scale-[0.98]"
          >
            <Mic className="mr-2 h-4 w-4" />
            Dicter le corrigé
          </Button>
          {recorder.activeLabel && (
            <p className="text-ink-400 text-center font-sans text-[11px]">
              Dernier micro utilisé : <span className="font-semibold">{recorder.activeLabel}</span>
            </p>
          )}
          {recorder.devices.length === 0 && (
            <p className="text-ink-400 text-center font-sans text-[11px]">
              Lance une 1re dictée pour autoriser le micro et lister les périphériques.
            </p>
          )}
        </div>
      )}

      {recorder.state === 'recording' && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-center gap-2 font-sans text-[13px] font-semibold text-rose-600">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500" />
            Enregistrement… {fmt(recorder.seconds)}
          </div>
          {/* VU-mètre : vérifie en direct que le micro capte du son */}
          <div className="flex items-center gap-2">
            <Mic className="text-ink-400 h-3.5 w-3.5 shrink-0" />
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-emerald-500 transition-[width] duration-75"
                style={{ width: `${Math.round(recorder.level * 100)}%` }}
              />
            </div>
          </div>
          <p className="text-ink-400 text-center font-sans text-[11px]">
            La barre doit bouger quand tu parles. Sinon, vérifie ton micro.
          </p>
          <Button
            type="button"
            onClick={() => void handleStop()}
            className="w-full active:scale-[0.98]"
          >
            <Square className="mr-2 h-4 w-4" />
            Terminer la dictée
          </Button>
        </div>
      )}

      {phase === 'transcribing' && (
        <div className="text-ink-600 flex items-center justify-center gap-2 py-3 font-sans text-[13px]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Transcription en cours…
        </div>
      )}

      {phase === 'transcript' && (
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-ink-700 mb-1 block font-sans text-[12px] font-semibold">
              Transcription (relis et corrige si besoin)
            </label>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={6}
              disabled={busy}
              className="border-ink-200 focus:border-primary-400 w-full rounded-xl border bg-white px-3 py-2 font-sans text-sm outline-none"
            />
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              onClick={reset}
              disabled={busy}
              className="min-h-11 active:scale-[0.98]"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Recommencer
            </Button>
            <Button
              type="button"
              onClick={() => void onStructure(transcript)}
              disabled={busy || transcript.trim().length < 10}
              className="w-full active:scale-[0.98] sm:flex-1"
            >
              {structuring ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {structureLabel}
            </Button>
          </div>
        </div>
      )}

      {shownError && <p className="mt-2 font-sans text-[12px] text-rose-600">{shownError}</p>}
    </div>
  );
}
